/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type NextApiRequest } from 'next';
import {
  type ContentStackDefaultPdp,
  type FormattedContentStackDefaultPDP,
} from '../../../interface';
import { getContentByContentType } from '../../common/getContentByContentType';
import { getLocaleForProxiedHostname, getProxiedHostname } from '@/utils/getInitialAppState';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { cache } from '@/utils/serverQueryCache';
import { deepFilterAndSegmentContent } from '../../../utils/deepFilterAndSegmentContent';
import { getDefaultPDPCacheKey } from '../../../utils/cache/getDefaultPDPCacheKey';

const CACHE_TIME = 1000 * 60 * 15;

export const getContentStackDefaultPDPContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[] = [],
): Promise<FormattedContentStackDefaultPDP> => {
  let response: ContentStackDefaultPdp | null;
  const proxiedHostname = getProxiedHostname(req);
  const language = getLocaleForProxiedHostname(proxiedHostname).toLowerCase();
  response = cache.getItem<ContentStackDefaultPdp>(getDefaultPDPCacheKey(language));

  if (!response) {
    response = await getContentByContentType<ContentStackDefaultPdp>({
      contentTypeUid: 'default_pdp',
      customizeQuery: ({ query: csQuery }) => {
        csQuery.addParam('include_all', 'true');
        return csQuery;
      },
      req,
      query,
    });

    cache.setItem(getDefaultPDPCacheKey(language), response, CACHE_TIME);
  }

  let pageBody: ContentStackDefaultPdp['pdp_page_body'] = response.pdp_page_body;
  const { prefooter } = response;
  const taxonomyPath = Array.isArray(query.taxonomyPath)
    ? `/${query.taxonomyPath.join('/')}`
    : query.taxonomyPath || '';

  pageBody = deepFilterAndSegmentContent({
    item: pageBody,
    taxonomyPath,
    userSegments,
    exactMatch: false,
  });

  return {
    pageBody,
    premiumBrands: response.brand_selector,
    partTypesWithVehicleImages: response.part_types_with_vehicle_images,
    prefooter,
  };
};
